import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams, HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom, catchError, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { CancelacionSitioRepository } from 'src/app/domain/service/cancelacion-sitio/cancelacion-sitio-repository';
import { validarSize } from 'src/app/common/validation-utils';
import { 
  CancelacionSitioEncabezado, 
  CancelacionSitioDetalle,
  CancelacionSitioFiltro, 
  CancelacionSitioPage, 
  CancelacionSitioRequest,
  EnvioNominaResponse,
  AgregarDetallesResponse,
  EliminarDetalleResponse
} from 'src/app/domain/model/cancelacion-sitio/cancelacion-sitio';

/**
 * Implementación HTTP del repositorio de Cancelaciones en Sitio.
 * 
 * @author Sura - Migración GWT a Angular
 * @version 1.0
 * @since Mayo 2026
 */
@Injectable({ providedIn: 'root' })
export class HttpCancelacionSitioRepository implements CancelacionSitioRepository {
  
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.backend.baseUrl}/servicios/cancelaciones-sitio`;
  
  async listar(
    filtro: CancelacionSitioFiltro,
    cursor: string | null,
    size: number,
    direction: 'NEXT' | 'PREVIOUS' | 'LAST'
  ): Promise<CancelacionSitioPage> {
    validarSize(size);
    let params = new HttpParams()
      .set('size', size.toString())
      .set('direction', direction);

    if (cursor) {
      params = params.set('cursor', cursor);
    }

    if (filtro.nombreEncabezado) {
      params = params.set('nombreEncabezado', filtro.nombreEncabezado);
    }
    if (filtro.nmconsecOficina) {
      params = params.set('nmconsecOficina', filtro.nmconsecOficina.toString());
    }
    if (filtro.anio) {
      params = params.set('anio', filtro.anio.toString());
    }
    if (filtro.mes) {
      params = params.set('mes', filtro.mes.toString());
    }
    
    return firstValueFrom(this.http.get<CancelacionSitioPage>(this.baseUrl, { params }));
  }
  
  async obtenerPorId(nmconsecutivo: number): Promise<CancelacionSitioEncabezado> {
    return firstValueFrom(this.http.get<CancelacionSitioEncabezado>(`${this.baseUrl}/${nmconsecutivo}`));
  }
  
  async crear(request: CancelacionSitioRequest): Promise<CancelacionSitioEncabezado> {
    return firstValueFrom(
      this.http.post<CancelacionSitioEncabezado>(this.baseUrl, request)
        .pipe(
          catchError((error: HttpErrorResponse) => {
            // Extraer mensaje del backend si existe
            const mensaje = error.error?.mensaje || error.message || 'Error al crear el encabezado';
            return throwError(() => new Error(mensaje));
          })
        )
    );
  }
  
  async actualizar(nmconsecutivo: number, request: CancelacionSitioRequest): Promise<CancelacionSitioEncabezado> {
    return firstValueFrom(
      this.http.put<CancelacionSitioEncabezado>(`${this.baseUrl}/${nmconsecutivo}`, request)
        .pipe(
          catchError((error: HttpErrorResponse) => {
            // Extraer mensaje del backend si existe
            const mensaje = error.error?.mensaje || error.message || 'Error al actualizar el encabezado';
            return throwError(() => new Error(mensaje));
          })
        )
    );
  }
  
  async anular(nmconsecutivo: number): Promise<void> {
    return firstValueFrom(
      this.http.delete<void>(`${this.baseUrl}/${nmconsecutivo}`)
        .pipe(
          catchError((error: HttpErrorResponse) => {
            // Extraer mensaje del backend si existe
            const mensaje = error.error?.mensaje || error.message || 'Error al eliminar el encabezado';
            return throwError(() => new Error(mensaje));
          })
        )
    );
  }
  
  async enviarANomina(nmconsecutivo: number): Promise<EnvioNominaResponse> {
    return firstValueFrom(
      this.http.post<EnvioNominaResponse>(`${this.baseUrl}/${nmconsecutivo}/enviar-nomina`, {})
    );
  }
  
  // === MÉTODOS DE DETALLES ===
  
  async listarDetalles(nmconsecEncabezado: number): Promise<CancelacionSitioDetalle[]> {
    return firstValueFrom(this.http.get<CancelacionSitioDetalle[]>(`${this.baseUrl}/${nmconsecEncabezado}/detalles`));
  }
  
  async listarProgramacionesDisponibles(nmconsecEncabezado: number): Promise<CancelacionSitioDetalle[]> {
    return firstValueFrom(
      this.http.get<CancelacionSitioDetalle[]>(`${this.baseUrl}/${nmconsecEncabezado}/programaciones-disponibles`)
        .pipe(
          catchError((error: HttpErrorResponse) => {
            const mensaje = error.error?.mensaje || error.message || 'Error al listar programaciones disponibles';
            return throwError(() => new Error(mensaje));
          })
        )
    );
  }
  
  async agregarDetalles(nmconsecEncabezado: number, nmconsecProgramaciones: number[]): Promise<AgregarDetallesResponse> {
    return firstValueFrom(
      this.http.post<AgregarDetallesResponse>(
        `${this.baseUrl}/${nmconsecEncabezado}/detalles`, 
        { nmconsecProgramaciones }
      ).pipe(
        catchError((error: HttpErrorResponse) => {
          const mensaje = error.error?.mensaje || error.message || 'Error al agregar detalles';
          return throwError(() => new Error(mensaje));
        })
      )
    );
  }
  
  async eliminarDetalle(nmconsecEncabezado: number, nmconsecDetalle: number): Promise<EliminarDetalleResponse> {
    return firstValueFrom(
      this.http.delete<EliminarDetalleResponse>(`${this.baseUrl}/${nmconsecEncabezado}/detalles/${nmconsecDetalle}`).pipe(
        catchError((error: HttpErrorResponse) => {
          const mensaje = error.error?.mensaje || error.message || 'Error al eliminar el detalle';
          return throwError(() => new Error(mensaje));
        })
      )
    );
  }
}
